import { Injectable } from '@angular/core';

/**
 * Service for managing device identification for multi-device session support.
 *
 * Generates and persists a unique device ID per browser/device instance.
 * This ID is sent with every HTTP request via the X-Device-Id header
 * to enable multi-device login functionality.
 *
 * The device ID is stored in localStorage to persist across browser sessions.
 */
@Injectable({ providedIn: 'root' })
export class DeviceIdService {
  private static readonly STORAGE_KEY = 'deviceId';
  private _deviceId: string | null = null;

  /**
   * Get the current device ID. Generates one if not already present.
   */
  get deviceId(): string {
    if (this._deviceId) {
      return this._deviceId;
    }

    // Try to get from localStorage first
    const storedDeviceId = localStorage.getItem(DeviceIdService.STORAGE_KEY);
    if (storedDeviceId) {
      this._deviceId = storedDeviceId;
      return storedDeviceId;
    }

    // Generate new device ID and persist it
    this._deviceId = this.generateDeviceId();
    localStorage.setItem(DeviceIdService.STORAGE_KEY, this._deviceId);
    return this._deviceId;
  }

  /**
   * Generate a new unique device ID.
   * Uses crypto.randomUUID() for secure random UUID generation.
   */
  private generateDeviceId(): string {
    // Use crypto.randomUUID() if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback to manual UUID v4 generation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, (c) => {
      const r = Math.trunc(Math.random() * 16);
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Clear the device ID from storage.
   * Useful when logging out from all devices.
   */
  clearDeviceId(): void {
    this._deviceId = null;
    localStorage.removeItem(DeviceIdService.STORAGE_KEY);
  }

  /**
   * Force regenerate the device ID.
   * Creates a new ID and stores it.
   */
  regenerateDeviceId(): string {
    this.clearDeviceId();
    return this.deviceId;
  }
}
